import mapValues from 'lodash/mapValues';
import find from 'lodash/find';
import merge from 'lodash/merge';
import {
    getItemId, getModelName, getModelOrNull, getSetCurrentModelId, getSubviewByModelName,
    getSubviewsByModelName, getRelationshipFieldsByModelName,
    getView
} from "~/stateGetter";
import {modify} from "mythil/object";
import modelTree from "~/selectors/modelTree";
import loadModels from "~/reducers/helpers/loadModels";
import ensureItemSubviewsAreLoaded from "~/reducers/helpers/ensureSubviewsAreLoaded";
import ensureRelatedModelsAreLoaded from "~/reducers/helpers/ensureRelatedModelsAreLoaded";
import parseSubviewModel from "~/navigation/parseSubviewModel";
import identify from "~/identify";
import {isTabularView, isItemView, presetActionPermissions} from "~/statePresenter";
import resetModelItemValues from "~/reducers/helpers/resetModelItemValues";
import clearMessages, {clearItemMessages} from "~/reducers/helpers/clearMessages";

let loadModelPathNr = 0;

const loadModelPath = (ui, path, changeRootModel, keepValues, state) => {
    let dummyTree = modelTree(ui.loadedModels);

    const modelDataFromToken = (token, parentModelId, modelId) => {
        let { modelName, itemId, mode, data = {} } = token;

        return {
            modelId,
            modelName,
            parentModelId,
            data: merge({
                loadModelPathNr,
                item: { id: itemId },
                previousView: null,
                view: mode,
            }, data)
        };
    };

    const modelDataFromSubviewToken = (token, parentModelId, modelId) => {
        let { itemId, mode, data = {} } = token,
            modelName = parseSubviewModel(token.modelName),
            subview = getSubviewByModelName(state, modelName, token.modelName);

        let modelData = {
            modelId,
            modelName,
            parentModelId,
            data: merge({
                loadModelPathNr,
                item: { id: itemId },
                previousView: null,
                view: mode,
            }, data)
        };

        if(!subview) {
            modelData = {
                ...modelData,
                data: {
                    ...modelData.data,
                    subviewAbbreviation: token.modelName
                }
            };

            console.log('XYZ modelDataFromSubviewToken no subview', modelData);

            return modelData;
        }

        let { name } = subview;
        modelData = {
            ...modelData,
            data: {
                ...modelData.data,
                subviewName: name
            }
        };

        return modelData;
    };

    let currentModelId = getSetCurrentModelId(state),
        loadedModels = changeRootModel || !currentModelId ? [] : [dummyTree.getRootModel(currentModelId)],
        modelsToLoad = [],
        parentModelId = null,
        differentiates = false,
        fittingModelIds = [];


    for (let i = 0; i < path.length; i++) {
        let modelToken = path[i];
        let fittingModel = null;

        if(!differentiates) {
            fittingModel = find(loadedModels, ({modelId}) => {
                let modelName = getModelName(state, modelId);
                return modelName === modelToken.modelName;
            });
        }

        if(fittingModel) {
            parentModelId = fittingModel.modelId;
            fittingModelIds.push(parentModelId);
            loadedModels = dummyTree.getChildren(parentModelId);

            if(getView(state, parentModelId) !== modelToken.mode) {
                ui = modify(ui, `loadedModels.${parentModelId}`, (model) => {
                    // dont change view if it is not intended
                    if(modelToken.isDefaultMode) {
                        if(modelToken.itemId && isItemView(model.view)) {
                            return model;
                        }

                        if(!modelToken.itemId && isTabularView(model.view)) {
                            return model;
                        }
                    }

                    if(modelToken.mode === model.view) { return model; }

                    if(!keepValues) {
                        model = resetModelItemValues(model);
                    }

                    const view = !!modelToken.itemId && !!presetActionPermissions(state, parentModelId, modelToken.itemId)?.update
                        ? identify.VIEW_EDIT
                        : modelToken.mode;

                    return {
                        ...model,
                        previousView: model.view,
                        view
                    };
                });
            }

            let itemId = getItemId(state, parentModelId);


            ui = modify(ui, `loadedModels.${parentModelId}`, (model) => {
                if(itemId !== modelToken.itemId) {
                    model = modify(model, `item.id`, modelToken.itemId);
                    model = resetModelItemValues(model);
                }

                if(itemId !== modelToken.itemId || modelToken.itemId === identify.NEW_ITEM) {
                    model = clearItemMessages(model);
                }

                return model;
            });

            ui = modify(ui, 'loadedModels', (loadedModels) => ensureItemSubviewsAreLoaded(loadedModels, {
                itemId: modelToken.itemId,
                parentModelId,
                modelName: fittingModel.modelName,
                getModelOrNull: (name) => getModelOrNull(state, name),
                getSubviewsByModelName: (name) => getSubviewsByModelName(state, name),
                getRelationshipFieldsByModelName: (name) => getRelationshipFieldsByModelName(state, name),
            }));


            ui = modify(ui, 'loadedModels', (loadedModels) => ensureRelatedModelsAreLoaded(loadedModels, {
                parentModelId,
                modelName: fittingModel.modelName,
                getModelOrNull: (name) => getModelOrNull(state, name),
                getSubviewsByModelName: (name) => getSubviewsByModelName(state, name),
                getRelationshipFieldsByModelName: (name) => getRelationshipFieldsByModelName(state, name),
            }));

            continue;
        }

        differentiates = true;

        let newModelId = dummyTree.getTreeId(modelToken.modelName, parentModelId);

        let modelToLoad = null;

        if(i === 0) {
            changeRootModel = true;

            modelToLoad = modelDataFromToken(
                modelToken,
                parentModelId,
                newModelId,
            );
        } else {
            modelToLoad = modelDataFromSubviewToken(
                modelToken,
                parentModelId,
                newModelId,
            );
        }

        dummyTree = dummyTree.loadModel(newModelId, modelToLoad, parentModelId);
        modelsToLoad.push(modelToLoad);
        parentModelId = newModelId;
    }

    if(!modelsToLoad.length) {
        ui = modify(ui, 'loadedModels', (loadedModels) => {
            return mapValues(loadedModels, (model) => {
                if (model.hasForeignFieldRelation) return model;

                if(model.parentModelId === parentModelId) {
                    return {
                        ...model,
                        previousView: model.view,
                        view: identify.VIEW_SUBVIEW,
                    }
                }

                return model;
            });
        });
    }

    ui = modify(ui, 'loadedModels', (loadedModels) => {
        // reset all messages in the model
        // loadedModels = mapValues(loadedModels, (model) => {
        //     return clearMessages(model);
        // });

        // reset items of all models that do not belong in the model path
        loadedModels = mapValues(loadedModels, (model) => {
            if(fittingModelIds.indexOf(model.modelId) > -1) {
                return model;
            }

            if (model.hasForeignFieldRelation)  {
                return model
            }

            if(model.item && model.item.id) {
                model = modify(model, 'item.id', null);
            }

            if(!keepValues) {
                model = resetModelItemValues(model);
            }

            return model;
        });

        if(modelsToLoad.length) {
            loadModelPathNr++;

            // load the new models
            loadedModels = loadModels(loadedModels, {
                models: modelsToLoad,
                getModelOrNull: (name) => getModelOrNull(state, name),
                getSubviewsByModelName: (name) => getSubviewsByModelName(state, name),
                getRelationshipFieldsByModelName: (name) => getRelationshipFieldsByModelName(state, name),
            });
        }

        // always keep the previous models and discard the ones before that
        for(let modelId in loadedModels) {
            if(!loadedModels.hasOwnProperty(modelId)) { continue; }
            const theLoadedModel = loadedModels[modelId];

            const parentModelId = theLoadedModel.parentModelId;
            if(parentModelId) { continue; }


            const theLoadModelPathNr = theLoadedModel.loadModelPathNr;

            if(theLoadModelPathNr <= loadModelPathNr-4) {
                loadedModels = modelTree(loadedModels).unloadModel(modelId).tree;
            }
        }

        // unload old models
        // loadedModels = modelTree(loadedModels).unloadModelsExcept(firstLoadedModel.modelId).tree;


        return loadedModels;
    });

    ui = modify(ui, 'currentModelId', parentModelId);

    return ui;
};

export default loadModelPath;